import apiClient from '../../../shared/api/apiClient';
import type {
  AdminDocumentPackageBundle,
  AdminDocumentPackageCreateInput,
  AdminDocumentPackageDetail,
  AdminDocumentPackageManifest,
  AdminDocumentPackagePage,
  AdminDocumentPackageSearchQuery,
  AdminDocumentPackageTimeline,
  AdminDocumentPackageVersions,
  AdminDocumentSignerPlanEntry,
  AdminLegalDocumentJobStatus,
  AdminLegalProfile,
  AdminLegalProfileUpdate,
  AdminLegalSignatureEvidence,
  AuthenticatedDownload,
  ETagged,
  LegalPackageCommand,
  LegalSignerCommand,
  SignedArtifactUploadInput,
} from '../legalTypes';

const requiredEtag = (headers: Record<string, unknown>): string => {
  const etag = headers.etag;
  if (typeof etag !== 'string' || etag.trim().length === 0) {
    throw Object.assign(new Error('The server response omitted its concurrency token.'), {
      code: 'etag_missing',
    });
  }
  return etag;
};

const etagged = <T>(data: T, headers: Record<string, unknown>): ETagged<T> => ({
  data,
  etag: requiredEtag(headers),
});

const mutationHeaders = (etag: string, idempotencyKey: string) => ({
  'Idempotency-Key': idempotencyKey,
  'If-Match': etag,
});

const fileNameFromDisposition = (disposition: unknown, fallback: string): string => {
  if (typeof disposition !== 'string') {
    return fallback;
  }
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition)?.[1];
  if (encoded) {
    try {
      return decodeURIComponent(encoded.replace(/^"|"$/g, ''));
    } catch {
      return fallback;
    }
  }
  return /filename="([^"]+)"/i.exec(disposition)?.[1]
    ?? /filename=([^;]+)/i.exec(disposition)?.[1]?.trim()
    ?? fallback;
};

export const downloadAuthenticatedBlob = async (
  path: string,
  fallbackFileName: string
): Promise<AuthenticatedDownload> => {
  const response = await apiClient.get<Blob>(path, { responseType: 'blob' });
  const fileName = fileNameFromDisposition(response.headers['content-disposition'], fallbackFileName);
  const objectUrl = URL.createObjectURL(response.data);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = fileName;
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  try {
    anchor.click();
  } finally {
    anchor.remove();
    URL.revokeObjectURL(objectUrl);
  }
  return { blob: response.data, fileName };
};

export const legalDocumentsApi = {
  searchPackages: async (
    query: AdminDocumentPackageSearchQuery,
    signal?: AbortSignal
  ): Promise<AdminDocumentPackagePage> => {
    const config = { params: query, signal } as Parameters<typeof apiClient.get>[1];
    const response = await apiClient.get<AdminDocumentPackagePage>(
      '/super-admin/document-packages',
      config
    );
    return response.data;
  },

  listCompanyPackages: async (
    companyId: number,
    page = 1,
    pageSize = 25
  ): Promise<AdminDocumentPackagePage> => {
    const response = await apiClient.get<AdminDocumentPackagePage>(
      `/super-admin/companies/${companyId}/document-packages`,
      { params: { page, pageSize } }
    );
    return response.data;
  },

  createPackage: async (
    companyId: number,
    input: AdminDocumentPackageCreateInput,
    idempotencyKey: string
  ): Promise<ETagged<AdminDocumentPackageDetail>> => {
    const response = await apiClient.post<AdminDocumentPackageDetail>(
      `/super-admin/companies/${companyId}/document-packages`,
      input,
      { headers: { 'Idempotency-Key': idempotencyKey } }
    );
    return etagged(response.data, response.headers);
  },

  getPackage: async (
    companyId: number,
    packageId: string
  ): Promise<ETagged<AdminDocumentPackageDetail>> => {
    const response = await apiClient.get<AdminDocumentPackageDetail>(
      `/super-admin/companies/${companyId}/document-packages/${packageId}`
    );
    return etagged(response.data, response.headers);
  },

  getManifest: async (
    companyId: number,
    packageId: string
  ): Promise<AdminDocumentPackageManifest> => {
    const response = await apiClient.get<AdminDocumentPackageManifest>(
      `/super-admin/companies/${companyId}/document-packages/${packageId}/manifest`
    );
    return response.data;
  },

  getVersions: async (
    companyId: number,
    packageId: string
  ): Promise<AdminDocumentPackageVersions> => {
    const response = await apiClient.get<AdminDocumentPackageVersions>(
      `/super-admin/companies/${companyId}/document-packages/${packageId}/versions`
    );
    return response.data;
  },

  getJobStatus: async (
    companyId: number,
    packageId: string
  ): Promise<AdminLegalDocumentJobStatus | null> => {
    const response = await apiClient.get<AdminLegalDocumentJobStatus | null>(
      `/super-admin/companies/${companyId}/document-packages/${packageId}/job-status`
    );
    return response.data;
  },

  getTimeline: async (
    companyId: number,
    packageId: string
  ): Promise<AdminDocumentPackageTimeline> => {
    const response = await apiClient.get<AdminDocumentPackageTimeline>(
      `/super-admin/companies/${companyId}/document-packages/${packageId}/events`
    );
    return response.data;
  },

  getSignatureEvidence: async (
    companyId: number,
    packageId: string
  ): Promise<AdminLegalSignatureEvidence[]> => {
    const response = await apiClient.get<AdminLegalSignatureEvidence[]>(
      `/super-admin/companies/${companyId}/document-packages/${packageId}/signature-evidence`
    );
    return response.data;
  },

  getPackageBundle: async (
    companyId: number,
    packageId: string
  ): Promise<AdminDocumentPackageBundle> => {
    const base = `/super-admin/companies/${companyId}/document-packages/${packageId}`;
    const [manifest, versions, job, timeline, evidence] = await Promise.all([
      apiClient.get<AdminDocumentPackageManifest>(`${base}/manifest`),
      apiClient.get<AdminDocumentPackageVersions>(`${base}/versions`),
      apiClient.get<AdminLegalDocumentJobStatus>(`${base}/job-status`),
      apiClient.get<AdminDocumentPackageTimeline>(`${base}/events`),
      apiClient.get<AdminLegalSignatureEvidence[]>(`${base}/signature-evidence`),
    ]);
    return {
      manifest: manifest.data,
      versions: versions.data,
      job: job.data,
      timeline: timeline.data,
      evidence: evidence.data,
    };
  },

  getLegalProfile: async (companyId: number): Promise<ETagged<AdminLegalProfile>> => {
    const response = await apiClient.get<AdminLegalProfile>(
      `/super-admin/companies/${companyId}/legal-profile`
    );
    return etagged(response.data, response.headers);
  },

  saveLegalProfile: async (
    companyId: number,
    input: AdminLegalProfileUpdate,
    etag: string
  ): Promise<ETagged<AdminLegalProfile>> => {
    const response = await apiClient.put<AdminLegalProfile>(
      `/super-admin/companies/${companyId}/legal-profile`,
      input,
      { headers: { 'If-Match': etag } }
    );
    return etagged(response.data, response.headers);
  },

  runPackageCommand: async (
    companyId: number,
    packageId: string,
    command: LegalPackageCommand,
    etag: string,
    idempotencyKey: string
  ): Promise<ETagged<AdminDocumentPackageDetail>> => {
    const response = await apiClient.post<AdminDocumentPackageDetail>(
      `/super-admin/companies/${companyId}/document-packages/${packageId}/${command}`,
      {},
      { headers: mutationHeaders(etag, idempotencyKey) }
    );
    return etagged(response.data, response.headers);
  },

  configureSignerPlan: async (
    companyId: number,
    packageId: string,
    signers: AdminDocumentSignerPlanEntry[],
    etag: string,
    idempotencyKey: string
  ): Promise<ETagged<AdminDocumentPackageDetail>> => {
    const response = await apiClient.put<AdminDocumentPackageDetail>(
      `/super-admin/companies/${companyId}/document-packages/${packageId}/signers`,
      { signers },
      { headers: mutationHeaders(etag, idempotencyKey) }
    );
    return etagged(response.data, response.headers);
  },

  runSignerCommand: async (
    companyId: number,
    packageId: string,
    signerId: string,
    command: LegalSignerCommand,
    etag: string,
    idempotencyKey: string
  ): Promise<ETagged<AdminDocumentPackageDetail>> => {
    const response = await apiClient.post<AdminDocumentPackageDetail>(
      `/super-admin/companies/${companyId}/document-packages/${packageId}/signers/${signerId}/${command}`,
      {},
      { headers: mutationHeaders(etag, idempotencyKey) }
    );
    return etagged(response.data, response.headers);
  },

  completeSigner: async (
    companyId: number,
    packageId: string,
    signerId: string,
    etag: string,
    idempotencyKey: string
  ): Promise<ETagged<AdminDocumentPackageDetail>> => {
    const response = await apiClient.post<AdminDocumentPackageDetail>(
      `/super-admin/companies/${companyId}/document-packages/${packageId}/signers/${signerId}/complete`,
      {},
      { headers: mutationHeaders(etag, idempotencyKey) }
    );
    return etagged(response.data, response.headers);
  },

  uploadSignedArtifact: async (
    companyId: number,
    packageId: string,
    signerId: string,
    input: SignedArtifactUploadInput,
    etag: string,
    idempotencyKey: string
  ): Promise<ETagged<AdminLegalSignatureEvidence>> => {
    const form = new FormData();
    form.append('file', input.file);
    form.append('declaredSignatoryName', input.declaredSignatoryName);
    form.append('authorityConfirmed', String(input.authorityConfirmed));
    form.append('signatureMethod', input.signatureMethod);
    const response = await apiClient.post<AdminLegalSignatureEvidence>(
      `/super-admin/companies/${companyId}/document-packages/${packageId}/signers/${signerId}/signed-artifact`,
      form,
      { headers: { ...mutationHeaders(etag, idempotencyKey), 'Content-Type': 'multipart/form-data' } }
    );
    return {
      data: response.data,
      etag: requiredEtag(response.headers),
    };
  },

  downloadPackage: (companyId: number, packageId: string) => downloadAuthenticatedBlob(
    `/super-admin/companies/${companyId}/document-packages/${packageId}/download`,
    `document-package-${packageId}.zip`
  ),

  downloadDocument: (companyId: number, packageId: string, documentId: string) =>
    downloadAuthenticatedBlob(
      `/super-admin/companies/${companyId}/document-packages/${packageId}/documents/${documentId}/download`,
      `document-${documentId}`
    ),

  downloadEvidence: (
    companyId: number,
    packageId: string,
    signerId: string,
    evidenceId: string
  ) => downloadAuthenticatedBlob(
    `/super-admin/companies/${companyId}/document-packages/${packageId}/signers/${signerId}/signed-artifacts/${evidenceId}`,
    `signed-evidence-${evidenceId}`
  ),
};
